import { IButtonStyles, IPlainCardProps, PersonaInitialsColor, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPIconButton, IPText } from 'baseComponents';
import { IPHoverCard, IPHoverCardType } from 'baseComponents/HoverCard/IPHoverCard';
import { IPList } from 'baseComponents/List/IPList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import HrefLink from 'components/Links/HrefLink';
import {
  cancelIcon,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import Group from 'models/group';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { sortOnString } from 'utils/sorting';
import UserTag from './UserTag';

interface IGroupTagProps {
  group: Group;
  compact?: boolean;
  hideBackground?: boolean;
  allowHoverCard: boolean;
  icon?: string;
  textColor?: string;
  onRemove?: (item: Group) => void;
}

const GroupTag = (props: IGroupTagProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'adminGroups']);
  const theme = getTheme();
  const size = props.compact ? IPPersonaSize.size24 : IPPersonaSize.size40;
  const name = props.group.name + (props.allowHoverCard && props.group.users ? ` (${props.group.users.length})` : '');

  const tagButtonStyles: IButtonStyles = {
    root: {
      borderRadius: '10px',
      height: props.compact ? '24px' : '40px',
      width: props.compact ? '24px' : '40px',
    },
  };

  const onRenderGroupCard = (item: Group): JSX.Element => {
    return (
      <IPStack
        styles={{
          root: { width: 300, minHeight: 100, height: 300, maxHeight: '50vh', padding: 20 },
        }}
      >
        <IPStack
          verticalFill
          tokens={globalStackTokensGapSmall}
          styles={{
            root: { overflow: 'scroll' },
          }}
        >
          <IPStack.Item>
            <IPText styles={globalTextStylesBold}>{t('adminGroups:Sync.Members')}</IPText>
          </IPStack.Item>
          {item.users && item.users.length > 0 && (
            <IPStack.Item>
              <IPList
                items={item.users.sort((a, b) => sortOnString(a.name, b.name))}
                onRenderCell={(user) => {
                  if (!user) return null;

                  return (
                    <IPStack.Item key={user.id} styles={{ root: { paddingBottom: 3 } }}>
                      <UserTag user={user} compact={true} hideBackground={props.hideBackground} />
                    </IPStack.Item>
                  );
                }}
              />
            </IPStack.Item>
          )}
          {(!item.users || item.users.length === 0) && <IPText>{t('adminGroups:Sync.NoMembers')}</IPText>}
        </IPStack>
        {hasUserFeature(appContext, FeatureTypes.AdminAuth) && (
          <IPStack.Item>
            <HrefLink href="/admin/groups">
              {item.syncEnabled ? t('adminGroups:Sync.EnabledInfo') : t('adminGroups:Sync.DisabledInfo')}
            </HrefLink>
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  const plainCardProps: IPlainCardProps = {
    onRenderPlainCard: onRenderGroupCard,
    renderData: props.group,
  };

  return (
    <IPHoverCard
      type={IPHoverCardType.plain}
      plainCardProps={plainCardProps}
      instantOpenOnClick={props.allowHoverCard && props.group.users !== undefined}
      shouldBlockHoverCard={() => !props.allowHoverCard || !props.group.users}
    >
      <IPStack
        horizontal
        tokens={globalStackTokensGapExtraSmall}
        verticalAlign="center"
        styles={
          props.hideBackground !== true
            ? {
                root: {
                  background: appContext.useDarkMode ? theme.palette.neutralSecondary : theme.palette.neutralLight,
                  borderRadius: '10px',
                  height: 1 + (props.compact ? 24 : 40),
                },
              }
            : undefined
        }
      >
        <IPStack.Item>
          <IPPersona
            text={name}
            secondaryText={props.group.email}
            size={size}
            hidePersonaDetails={false}
            initialsColor={props.icon ? PersonaInitialsColor.transparent : undefined}
            initialsTextColor={props.icon ? theme.palette.themePrimary : undefined}
            styles={{ primaryText: { color: props.textColor } }}
            onRenderInitials={
              props.icon
                ? () => {
                    return <IPFontIcon iconName={props.icon} style={{ fontSize: '20px' }} />;
                  }
                : undefined
            }
          />
        </IPStack.Item>
        {props.onRemove && (
          <IPStack.Item>
            <IPIconButton
              onClick={() => {
                if (props.onRemove) {
                  props.onRemove(props.group);
                }
              }}
              styles={tagButtonStyles}
              iconProps={cancelIcon}
            />
          </IPStack.Item>
        )}
      </IPStack>
    </IPHoverCard>
  );
};

export default GroupTag;
